import { computed, ref } from "vue"
import type { Ref } from "vue"
import { CheckCircleIcon } from "@heroicons/vue/solid"
import {
  CashIcon,
  ExternalLinkIcon,
  UserGroupIcon,
  DesktopComputerIcon,
  BookOpenIcon,
  BriefcaseIcon,
  FlagIcon,
  UserAddIcon,
  UsersIcon,
  PlusIcon,
  AcademicCapIcon,
  ViewGridAddIcon,
  OfficeBuildingIcon,
  ArrowCircleRightIcon,
  MailIcon,
  NewspaperIcon,
} from "@heroicons/vue/outline"
import facebookIcon from "@/components/find-an-advisor/shared/IconFacebook.vue"
import twitterIcon from "@/components/find-an-advisor/shared/IconTwitter.vue"
import instagramIcon from "@/components/find-an-advisor/shared/IconInstagram.vue"
import youtubeIcon from "@/components/find-an-advisor/shared/IconYouTube.vue"
import linkedinIcon from "@/components/find-an-advisor/shared/IconLinkedin.vue"
import {
  FindAnAdvisorProfile,
  AdvisorProfileListItem,
  CommunityProfile,
} from "@/types/profile"
import { linkRedirectFormat } from "@/composables/analytics"
import type { IconListItem } from "@/components/shared/IconList.vue"
import { NullDate } from "@/types"

export const getFirmImageURL = (slug: string, updatedAt?: NullDate) => {
  return slug
    ? `${import.meta.env.VITE_APP_S3_PUBLIC_URL}/${slug}/firm-logo?t=${
        updatedAt || Date.now()
      }`
    : ""
}

export const getProfileImageURL = (slug: string, updatedAt?: NullDate) => {
  return slug
    ? `${
        import.meta.env.VITE_APP_S3_PUBLIC_URL
      }/${slug}/advisor-profile-headshot?t=${updatedAt || Date.now()}`
    : ""
}

export const useFindAnAdvisorProfile = (
  ap: FindAnAdvisorProfile | Ref<FindAnAdvisorProfile>
) => {
  const profile = ref(ap)

  const bioGraphs = computed(() => {
    return profile.value.bioLong.split(/\r?\n|\r|\n/g)
  })

  const profileImageURL = computed(() => {
    return getProfileImageURL(profile.value.slug, profile.value.updatedAt)
  })

  const firmLogoURL = computed(() => {
    return getFirmImageURL(profile.value.slug, profile.value.updatedAt)
  })

  const idealClientsList = computed((): AdvisorProfileListItem[] => {
    return [
      profile.value.nicheGeneration,
      profile.value.nichePrimary,
      profile.value.nicheSecondary,
      profile.value.nicheTertiary,
    ]
      .filter((n) => n && n.niche)
      .map((n) => {
        return {
          icon: CheckCircleIcon,
          value: n.niche,
        }
      })
  })

  const feeOptionsList = computed((): AdvisorProfileListItem[] => {
    return [
      {
        icon: CheckCircleIcon,
        data: profile.value.feeFlat,
        value: `Flat Rate: ${profile.value.feeFlat}`,
      },
      {
        icon: CheckCircleIcon,
        data: profile.value.feeHourly,
        value: `Hourly: ${profile.value.feeHourly}`,
      },
      {
        icon: CheckCircleIcon,
        data: profile.value.feeMonthly,
        value: `Monthly: ${profile.value.feeMonthly}`,
      },
      {
        icon: CheckCircleIcon,
        data: profile.value.feeQuarterly,
        value: `Quarterly: ${profile.value.feeQuarterly}`,
      },
      {
        icon: CheckCircleIcon,
        data: profile.value.feeAUM,
        value: `AUM: ${profile.value.feeAUM}`,
      },
      {
        icon: CheckCircleIcon,
        data: profile.value.feeAdditional,
        value: `Additional: ${profile.value.feeAdditional}`,
      },
    ].filter((f) => f.data !== "")
  })

  const mediaMentionsList = computed((): AdvisorProfileListItem[] => {
    return [
      {
        icon: NewspaperIcon,
        link: profile.value.mentionUrlPrimary,
        value: profile.value.mentionTitlePrimary,
      },
      {
        icon: NewspaperIcon,
        link: profile.value.mentionUrlSecondary,
        value: profile.value.mentionTitleSecondary,
      },
      {
        icon: NewspaperIcon,
        link: profile.value.mentionUrlTertiary,
        value: profile.value.mentionTitleTertiary,
      },
    ].filter((f) => f.link !== "" && f.value !== "")
  })

  const publisherLogos = computed(() => {
    const imageAssets = publisherImageURLs()

    if (!profile.value.publisherMentions) {
      return []
    }

    return profile.value.publisherMentions
      .map((p) => {
        return imageAssets[p] || null
      })
      .filter((p) => p)
  })

  const secRecordsList = computed((): AdvisorProfileListItem[] => {
    const items = []

    if (profile.value.secFirmRegUrl) {
      items.push({
        icon: ExternalLinkIcon,
        value: "Firm Registration",
        link: profile.value.secFirmRegUrl,
      })
    }

    if (profile.value.secAdvisorRegUrl) {
      items.push({
        icon: ExternalLinkIcon,
        value: "Advisor Registration",
        link: profile.value.secAdvisorRegUrl,
      })
    }

    if (profile.value.advUrl) {
      items.push({
        icon: ExternalLinkIcon,
        value: "Form ADV",
        link: profile.value.advUrl,
      })
    }

    return items
  })

  const socialsList = computed(() => {
    return [
      {
        platform: "facebook",
        url: profile.value.facebookUrl,
        icon: facebookIcon,
      },
      {
        platform: "twitter",
        url: profile.value.twitterUrl,
        icon: twitterIcon,
      },
      {
        platform: "linkedin",
        url: profile.value.linkedInUrl,
        icon: linkedinIcon,
      },
      {
        platform: "youtube",
        url: profile.value.youTubeUrl,
        icon: youtubeIcon,
      },
      {
        platform: "instagram",
        url: profile.value.instagramUrl,
        icon: instagramIcon,
      },
    ].filter((s) => s.url)
  })

  return {
    bioGraphs,
    feeOptionsList,
    firmLogoURL,
    idealClientsList,
    mediaMentionsList,
    profileImageURL,
    publisherLogos,
    secRecordsList,
    socialsList,
  }
}

export const useCommunityProfile = (
  ap: CommunityProfile | Ref<CommunityProfile>
) => {
  const profile = ref(ap)
  const faaProfile = useFindAnAdvisorProfile(profile)

  const descriptionGraphs = computed(() => {
    return profile.value.descriptionCommunity.split(/\r?\n|\r|\n/g)
  })

  const expertiseList = computed((): IconListItem[] => {
    if (!profile.value.techExpertise) {
      return []
    }

    return profile.value.techExpertise.map((tech) => {
      return {
        title: tech,
        icon: DesktopComputerIcon,
        iconBackground: "bg-gray-50",
        iconColor: "text-cyan-800/80",
      }
    })
  })

  const feesList = computed((): IconListItem[] => {
    return [
      {
        icon: CashIcon,
        title: "Flat Rate",
        description: profile.value.feeFlat || "",
      },
      {
        icon: CashIcon,
        title: "Hourly",
        description: profile.value.feeHourly || "",
      },
      {
        icon: CashIcon,
        title: "Monthly",
        description: profile.value.feeMonthly || "",
      },
      {
        icon: CashIcon,
        title: "Quarterly",
        description: profile.value.feeQuarterly || "",
      },
      {
        icon: CashIcon,
        title: "AUM",
        description: profile.value.feeAUM || "",
      },
      {
        icon: CashIcon,
        title: "Additional Fees",
        description: profile.value.feeAdditional || "",
      },
    ]
      .filter((fee) => fee.description !== "")
      .map((fee) => {
        return {
          ...fee,
          iconBackground: "bg-gray-50",
          iconColor: "text-green-800/80",
        }
      })
  })

  const interestsList = computed(() => {
    return [
      {
        title: "Job Seeking",
        description: "Looking to be hired",
        icon: BriefcaseIcon,
        iconBackground: "bg-amber-700",
        iconColor: "text-amber-50",
        enabled: profile.value.hasEmploymentInterest,
      },
      {
        title: "Hiring",
        description: "Looking to hire a financial planner",
        icon: UserAddIcon,
        iconBackground: "bg-amber-700",
        iconColor: "text-amber-50",
        enabled: profile.value.hasHiringInterest,
      },
      {
        title: "Buying",
        description: "Interested in buying a firm",
        icon: PlusIcon,
        iconBackground: "bg-sky-700",
        iconColor: "text-sky-50",
        enabled: profile.value.hasPurchaseInterest,
      },
      {
        title: "Selling",
        description: "Open to selling firm",
        icon: FlagIcon,
        iconBackground: "bg-sky-700",
        iconColor: "text-sky-50",
        enabled: profile.value.hasSaleInterest,
      },
      {
        title: "Merging",
        description: "Open to merging firms",
        icon: ViewGridAddIcon,
        iconBackground: "bg-sky-700",
        iconColor: "text-sky-50",
        enabled: profile.value.hasMergeInterest,
      },
      {
        title: "Succession",
        description: "Looking for succession plan partner",
        icon: UsersIcon,
        iconBackground: "bg-sky-700",
        iconColor: "text-sky-50",
        enabled: profile.value.hasSuccessionInterest,
      },
      {
        title: "Mentoring",
        description: "Open to being a mentor",
        icon: BookOpenIcon,
        iconBackground: "bg-emerald-700",
        iconColor: "text-emerald-50",
        enabled: profile.value.hasMentorInterest,
      },
      {
        title: "Apprenticeship",
        description: "Looking for a mentor",
        icon: AcademicCapIcon,
        iconBackground: "bg-emerald-700",
        iconColor: "text-emerald-50",
        enabled: profile.value.hasMenteeInterest,
      },
    ].filter((i) => i.enabled)
  })

  const nichesList = computed((): IconListItem[] => {
    return [
      profile.value.nicheGeneration,
      profile.value.nichePrimary,
      profile.value.nicheSecondary,
      profile.value.nicheTertiary,
    ]
      .filter((n) => n && n.niche)
      .map((n) => {
        return {
          title: n.niche,
          description: n.category,
          icon: UserGroupIcon,
          iconBackground: "bg-gray-50",
          iconColor: "text-xy-blue-800/80",
        }
      })
  })

  const connectMethods = computed((): IconListItem[] => {
    // General Networking
    // Business Interests
    // Tech Expertise
    // Mentorship
    // Send Referral
    return [
      {
        topic: "general-networking",
        title: "General Networking",
        icon: MailIcon,
        iconBackground: "bg-xy-blue/90",
      },
      {
        topic: "business-interests",
        title: "Business Interests",
        icon: OfficeBuildingIcon,
        iconBackground: "bg-amber-700",
      },
      {
        topic: "tech-expertise",
        title: "Tech Expertise",
        icon: DesktopComputerIcon,
        iconBackground: "bg-pink-700",
      },
      {
        topic: "mentorship",
        title: "Mentorship",
        icon: AcademicCapIcon,
        iconBackground: "bg-indigo-700",
      },
      {
        topic: "referral",
        title: "Send Referral",
        icon: ArrowCircleRightIcon,
        iconBackground: "bg-green-700",
      },
    ].map((m) => {
      return {
        ...m,
        iconColor: "text-gray-50",
        url: `/community/profiles/${profile.value.slug}/connect?topic=${m.topic}`,
      }
    })
  })

  return {
    ...faaProfile,
    connectMethods,
    descriptionGraphs,
    expertiseList,
    feesList,
    interestsList,
    nichesList,
  }
}

export const outboundCapture = (url: string, newWindow = false) => {
  setTimeout(() => {
    const location = linkRedirectFormat(url, "find-an-advisor")
    if (newWindow) {
      window.open(location)
    } else {
      document.location = location
    }
  }, 150)
}

export const personaOptions = [
  {
    value: "breakout",
    label: "I'm a fee-only advisor thinking about breaking out on my own",
  },
  {
    value: "broker",
    label: "My background is in wirehouse, B/D, or insurance",
  },
  {
    value: "career-changer",
    label: "I'm leaving another industry to work in financial planning",
  },
  {
    value: "gen-xy",
    label: "My firm is interested in adding services for Gen X/Y",
  },
  { value: "multi-advisor", label: "I joined an existing XYPN member firm" },
  { value: "owner", label: "I own an RIA" },
  { value: "student", label: "I am a full-time college student" },
  { value: "industry", label: "None of the above" },
]

/**
 * publisherOptions is set of publishers that a member may select
 * as part of the "featured media" section of their AdvisorProfile.
 */
export const publisherOptions = [
  { label: "Bloomberg", value: "bloomberg" },
  { label: "Business Insider", value: "business-insider" },
  { label: "CBS News", value: "cbs-news" },
  { label: "CNBC", value: "cnbc" },
  { label: "ETF.com", value: "etf-dotcom" },
  { label: "Financial Advisor Magazine", value: "fa-mag" },
  { label: "Financial Planning", value: "financial-planning" },
  { label: "Forbes", value: "forbes" },
  { label: "Fortune", value: "fortune" },
  { label: "Investment News", value: "investment-news" },
  { label: "MarketWatch", value: "marketwatch" },
  { label: "MoneyGeek", value: "moneygeek" },
  { label: "Nasdaq", value: "nasdaq" },
  { label: "TheStreet", value: "thestreet" },
  { label: "The Wall Street Journal", value: "wall-street-journal" },
  { label: "ThinkAdvisor", value: "thinkadvisor" },
  { label: "Time", value: "time" },
  { label: "U.S. News & World Report", value: "us-news" },
  { label: "USA Today", value: "usa-today" },
  { label: "Yahoo Finance", value: "yahoo-finance" },
]

/**
 * Get a map of all publisher image URLs in the assets directory where the
 * key is the slug of the publisher and the value is the image URL.
 * [docs](https://vitejs.dev/guide/features.html#glob-import-as)
 * @returns Record<string, string>
 */
function publisherImageURLs() {
  // NOTE(spk): the Vite glob feature requires a relative path
  const assets = import.meta.glob<string>("../assets/publishers/*", {
    eager: true,
    import: "default",
    query: "?url",
  })

  const publisherImages: Record<string, string> = {}

  for (const [path, url] of Object.entries(assets)) {
    // replace relative path, replace extension
    publisherImages[path.replace(/^.*[\\/]/, "").replace(/\.[^/.]+$/, "")] = url
  }

  return publisherImages
}
